import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { previewMenuToDateMenuConcept } from '@utils/groupOrderUtils'
import { ClientSettings, PreviewMenu } from '@types'
import {
  MenuConcept,
  SearchGroupOrderConceptsFilters,
  SearchGroupOrderConceptsProps,
  MenuItemAttributes,
} from './types'
import { StagedMenusSection } from './StagedMenus'
import { UniversalMenuSelector } from './UniversalMenuSelector'
import { LegacyMenuSelector } from './LegacyMenuSelector'

interface PickMenuModalProps {
  date: moment.Moment
  clientSettings: ClientSettings
  searchMenuConcepts: (
    req: SearchGroupOrderConceptsProps,
  ) => Promise<MenuConcept[]>
  loadMenuItemSettings: () => Promise<MenuItemAttributes>
  onPickMenu: (menuConcept: MenuConcept) => Promise<void>
}

const PickMenuModal: React.FC<PickMenuModalProps> = ({
  date,
  clientSettings,
  loadMenuItemSettings,
  onPickMenu,
  searchMenuConcepts,
}) => {
  const [search, setSearch] = useState('')
  const [menuConcepts, setMenuConcepts] = useState<MenuConcept[]>([])
  const [menuItemAttributes, setMenuItemAttributes] =
    useState<MenuItemAttributes>({ allDietaryPreferences: [] })
  const [isLoading, setIsLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [showDietaryCounts, setShowDietaryCounts] = useState(false)
  const [expandedLegacy, setExpandedLegacy] = useState<Set<string>>(new Set())
  const [showUniversalMenuSelector, toggleShowUniversalMenuSelector] =
    useState(false)
  const [stagedMenus, setStagedMenus] = useState<MenuConcept[]>([])
  const [isProcessingStaged, setIsProcessingStaged] = useState(false)
  const [filteredDietaryPreference, setFilteredDietaryPreference] =
    useState<string>('')
  const [
    filteredExcludeTagsDietaryPreferenceUnique,
    setFilteredExcludeTagsDietaryPreferenceUnique,
  ] = useState<string>('')
  const [filterExcludeTags, setFilterExcludeTags] = useState(false)
  const [lastSearchFilters, setLastSearchFilters] =
    useState<SearchGroupOrderConceptsFilters | null>(null)
  const limit = 25

  const hasUniversalMenuCfg = Boolean(clientSettings.universalMenuConfig)

  const performSearch = async (
    searchTerm: string,
    pageNum: number,
    dietaryFilter: string,
    excludeTagsDietaryPreferenceUnique: string,
  ) => {
    setIsLoading(true)
    const searchFilters: SearchGroupOrderConceptsFilters = {
      search: searchTerm,
      availableOn: date.toISOString(),
      tagsDietaryPreferenceUnique: dietaryFilter,
      excludeTagsDietaryPreferenceUnique,
    }
    let nextPage = pageNum
    if (JSON.stringify(searchFilters) !== JSON.stringify(lastSearchFilters)) {
      nextPage = 0
      setPage(nextPage)
    }
    const results = await searchMenuConcepts({
      ...searchFilters,
      page: nextPage,
      limit,
      isUniversal: false,
    })
    if (results) {
      setMenuConcepts(results)
    } else {
      setMenuConcepts([])
    }
    setLastSearchFilters(searchFilters)
    setIsLoading(false)
  }

  useEffect(() => {
    loadMenuItemSettings().then((attributes) => {
      setMenuItemAttributes(attributes)
    })
  }, [loadMenuItemSettings])

  useEffect(() => {
    performSearch('', 0, '', '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const expandAll = (
    setFn: React.Dispatch<React.SetStateAction<Set<string>>>,
    menus: MenuConcept[],
  ) => setFn(new Set(menus.map((m) => m.id)))

  const collapseAll = (
    setFn: React.Dispatch<React.SetStateAction<Set<string>>>,
  ) => setFn(new Set())

  const toggleExpanded =
    (set: React.Dispatch<React.SetStateAction<Set<string>>>) =>
    (id: string) => {
      set((prev) => {
        const next = new Set(prev)
        next.has(id) ? next.delete(id) : next.add(id)

        return next
      })
    }

  const handleSearchSubmit = () => {
    performSearch(
      search,
      page,
      filteredDietaryPreference,
      filteredExcludeTagsDietaryPreferenceUnique,
    )
  }

  const handleStageMenu = (menu: MenuConcept) => {
    if (!stagedMenus.find((staged) => staged.id === menu.id)) {
      setStagedMenus((prev) => [...prev, menu])
    }
  }

  const handleStageUniversalMenu = (
    m: PreviewMenu,
    excludedItemIds: Set<string>,
  ) => {
    const menuConcept: MenuConcept = previewMenuToDateMenuConcept(
      m,
      excludedItemIds,
    )
    if (!stagedMenus.find((staged) => staged.id === menuConcept.id)) {
      setStagedMenus((prev) => [...prev, menuConcept])
    }
  }

  const handleRemoveFromStaging = (menuId: string) => {
    setStagedMenus((prev) => prev.filter((menu) => menu.id !== menuId))
  }

  const handleFinalizeStaged = async () => {
    if (stagedMenus.length === 0) {
      return
    }

    setIsProcessingStaged(true)

    try {
      for (const menu of stagedMenus) {
        await onPickMenu(menu)
      }

      // Clear staged menus after successful processing
      setStagedMenus([])
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`handleFinalizeStaged error: ${error}`)
    } finally {
      setIsProcessingStaged(false)
    }
  }

  const dietaryPreferenceOptions = [
    { value: '', text: 'All' },
    ...menuItemAttributes.allDietaryPreferences.map((preference) => ({
      value: preference,
      text: preference,
    })),
  ]

  const excludeDietaryPreferenceOptions = [
    { value: '', text: 'None' },
    ...menuItemAttributes.allDietaryPreferences.map((preference) => ({
      value: preference,
      text: preference,
    })),
  ]

  const updateDietaryPreferenceFilters = (dietaryTag: string) => {
    if (filterExcludeTags) {
      setFilteredExcludeTagsDietaryPreferenceUnique(dietaryTag)
      setFilteredDietaryPreference('')
    } else {
      setFilteredDietaryPreference(dietaryTag)
      setFilteredExcludeTagsDietaryPreferenceUnique('')
    }
  }

  return (
    <div className="w-full">
      {/* Need to have Universal Menu Cfg to use Universal Menu Selector */}
      {hasUniversalMenuCfg ? (
        <div className="flex flex-row items-center justify-between">
          <label className="flex items-center cursor-pointer">
            <div className="relative" style={{ width: '60px', height: '32px' }}>
              <input
                type="checkbox"
                checked={showUniversalMenuSelector}
                onChange={() =>
                  toggleShowUniversalMenuSelector(!showUniversalMenuSelector)
                }
                className="sr-only"
              />
              <div
                className="rounded-full absolute inset-0"
                style={{
                  backgroundColor: showUniversalMenuSelector
                    ? '#38b2ac'
                    : '#ff624d',
                  transition: 'background-color 0.3s ease-in-out',
                }}
              />
              <div
                className="rounded-full bg-white absolute"
                style={{
                  height: '24px',
                  width: '24px',
                  top: '4px',
                  left: showUniversalMenuSelector ? '32px' : '4px',
                  transition: 'left 0.3s ease-in-out',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  opacity: '1',
                }}
              />
            </div>
            <span className="ml-3 bold font-medium text-gray-700">
              {showUniversalMenuSelector
                ? 'Universal Menu Selector'
                : 'Standard Menu Selector'}
            </span>
          </label>
        </div>
      ) : (
        <label>
          Please create a universal menu configuration at the client settings
          level to utilize the universal menu selector
        </label>
      )}
      <StagedMenusSection
        stagedMenus={stagedMenus}
        isLoading={isLoading}
        isProcessing={isProcessingStaged}
        onRemove={handleRemoveFromStaging}
        onFinalize={handleFinalizeStaged}
      />
      {showUniversalMenuSelector ? (
        <UniversalMenuSelector
          date={date}
          clientSettings={clientSettings}
          stagedMenus={stagedMenus}
          onSelectMenu={handleStageUniversalMenu}
        />
      ) : (
        <LegacyMenuSelector
          collapseAll={collapseAll}
          dietaryPreferenceOptions={dietaryPreferenceOptions}
          excludeDietaryPreferenceOptions={excludeDietaryPreferenceOptions}
          expandAll={expandAll}
          expandedLegacy={expandedLegacy}
          filteredDietaryPreference={filteredDietaryPreference}
          filteredExcludeTagsDietaryPreferenceUnique={
            filteredExcludeTagsDietaryPreferenceUnique
          }
          filterExcludeTags={filterExcludeTags}
          handleSearchSubmit={handleSearchSubmit}
          handleStageMenu={handleStageMenu}
          isLoading={isLoading}
          limit={limit}
          menuConcepts={menuConcepts}
          page={page}
          performSearch={performSearch}
          search={search}
          setExpandedLegacy={setExpandedLegacy}
          setFilterExcludeTags={setFilterExcludeTags}
          setFilteredDietaryPreference={setFilteredDietaryPreference}
          setFilteredExcludeTagsDietaryPreferenceUnique={
            setFilteredExcludeTagsDietaryPreferenceUnique
          }
          setPage={setPage}
          setSearch={setSearch}
          setShowDietaryCounts={setShowDietaryCounts}
          showDietaryCounts={showDietaryCounts}
          stagedMenus={stagedMenus}
          toggleExpanded={toggleExpanded}
          updateDietaryPreferenceFilters={updateDietaryPreferenceFilters}
        />
      )}
    </div>
  )
}

export default PickMenuModal
